import { useState } from 'react'
import { AppBar, Toolbar, Typography, Box, IconButton } from '@mui/material'
import { Brightness4, Brightness7, AutoFixHigh } from '@mui/icons-material'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { useTheme } from '../../context/ThemeContext'
import { useAuth } from '../../context/AuthContext'
import { designSpecs } from '../../theme/designSpecs'
import Button from '../common/Button'
import AuthModal from '../auth/AuthModal'

const Navbar = () => {
  const { mode, toggleTheme } = useTheme()
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [authMode, setAuthMode] = useState('signup')

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <AppBar
      component="nav"
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid',
        borderColor: 'grey.100',
        boxShadow: designSpecs.shadows.sm,
      }}
    >
      <Toolbar sx={{ height: 80, maxWidth: '80rem', width: '100%', mx: 'auto', px: { xs: 2, sm: 3, lg: 4 } }}>
        {/* Logo */}
        <Box
          component={RouterLink}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            textDecoration: 'none',
            flexGrow: 1,
            transition: designSpecs.transitions.default,
            '&:hover .logo-icon': {
              transform: 'scale(1.05)',
            },
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <Box
              className="logo-icon"
              sx={{
                width: 44,
                height: 44,
                background: designSpecs.gradients.primary,
                borderRadius: designSpecs.borderRadius.lg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 3,
                transition: designSpecs.transitions.default,
              }}
            >
              <AutoFixHigh sx={{ color: 'white', fontSize: designSpecs.iconSizes.lg }} />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                right: -4,
                width: 12,
                height: 12,
                bgcolor: 'warning.main',
                borderRadius: '50%',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    opacity: 1,
                  },
                  '50%': {
                    opacity: 0.5,
                  },
                },
              }}
            />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              background: designSpecs.gradients.primary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: designSpecs.typography.sizes['2xl'],
            }}
          >
            Mocksy
          </Typography>
        </Box>

        {/* Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 4 }}>
            {location.pathname === '/' ? (
              <>
                <Typography
                  onClick={() => scrollToSection('pricing')}
                  sx={{
                    color: 'grey.600',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: designSpecs.typography.sizes.base,
                    transition: designSpecs.transitions.default,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  Pricing
                </Typography>
                <Typography
                  onClick={() => scrollToSection('ideas')}
                  sx={{
                    color: 'grey.600',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: designSpecs.typography.sizes.base,
                    transition: designSpecs.transitions.default,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  Ideas
                </Typography>
              </>
            ) : (
              <Typography
                component={RouterLink}
                to="/"
                sx={{
                  color: 'grey.600',
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: designSpecs.typography.sizes.base,
                  transition: designSpecs.transitions.default,
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                Home
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!isAuthenticated && (
              <Button
                variant="text"
                onClick={() => {
                  setAuthMode('login')
                  setAuthModalOpen(true)
                }}
                sx={{
                  color: 'grey.600',
                  fontWeight: 500,
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'transparent',
                    transform: 'none',
                  },
                }}
              >
                Login
              </Button>
            )}
            <Button
              variant="contained"
              component={isAuthenticated ? RouterLink : 'button'}
              to={isAuthenticated ? '/dashboard' : undefined}
              onClick={isAuthenticated ? undefined : () => {
                setAuthMode('signup')
                setAuthModalOpen(true)
              }}
              sx={{
                background: designSpecs.gradients.primary,
                fontWeight: 600,
                px: 3,
                py: 1.25,
                boxShadow: '0 10px 15px -3px rgba(99, 102, 241, 0.3)',
                '&:hover': {
                  background: designSpecs.gradients.primary,
                  boxShadow: '0 20px 25px -5px rgba(99, 102, 241, 0.4)',
                },
              }}
            >
              {isAuthenticated ? 'Dashboard' : 'Generate Your First Photo'}
            </Button>
          </Box>
        </Box>
      </Toolbar>
      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        defaultMode={authMode}
      />
    </AppBar>
  )
}

export default Navbar
